import type { TvState } from '@akpl/shared';
import { useEffect, useState } from 'react';

/** The banner only appears after this long offline, so blips stay invisible (docs/04 §5). */
export const DISCONNECT_GRACE_MS = 5000;

/** Twice the engine's keep-alive: one lost write must not call the screen disconnected. */
export const SCREEN_SILENT_MS = 30_000;

/** E-25: this tab lost the database for longer than a blip. The video keeps playing. */
export function useReconnecting(connected: boolean, active: boolean): boolean {
  const [wasConnected, setWasConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);

  // `active` in both: connected with the session over (E-24), one undoing the other never settles.
  if (active && connected && !wasConnected) setWasConnected(true);
  if (!active && wasConnected) setWasConnected(false);
  if (reconnecting && (connected || !active)) setReconnecting(false);

  useEffect(() => {
    if (!active || connected || !wasConnected) return undefined;
    const timer = setTimeout(() => setReconnecting(true), DISCONNECT_GRACE_MS);
    return () => clearTimeout(timer);
  }, [active, connected, wasConnected]);

  return reconnecting;
}

/**
 * ME-13: the screen stopped writing `tvState`. Measured by when the value *arrived* here, never by
 * its `updatedAt`, because that is the other machine's clock. A phone that is itself offline
 * cannot tell, so it says nothing; the value coming back clears it on its own.
 */
export function useScreenSilent(
  tvState: TvState | null,
  connected: boolean,
  active: boolean,
): boolean {
  const [silent, setSilent] = useState(false);
  const [lastSeen, setLastSeen] = useState(tvState);
  if (lastSeen !== tvState) {
    setLastSeen(tvState);
    if (silent) setSilent(false);
  }
  if (silent && (!active || !connected)) setSilent(false);

  useEffect(() => {
    if (!active || !connected) return undefined;
    const timer = setTimeout(() => setSilent(true), SCREEN_SILENT_MS);
    return () => clearTimeout(timer);
  }, [active, connected, tvState]);

  return silent;
}
